import { motion } from "framer-motion";
import { Link } from "wouter";
import { ArrowRight, Code2, Zap, Rocket, Terminal, Users, Cpu } from "lucide-react";
import { SEO } from "@/components/SEO";
import { JsonLd } from "@/components/JsonLd";

export default function About() {

  return (
    <div className="flex flex-col w-full overflow-hidden">
      <SEO
        title="About Our Studio"
        description="Learn about the story behind AccelerateByte. We are an elite technical strike force based in Mumbai, serving global clients with surgical speed and precision."
      />
      <JsonLd schema={[
        {
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "About AccelerateByte",
          "url": "https://acceleratebyte.com/about",
          "description": "We founded AccelerateByte in 2025 to be the elite technical partner we wished existed — surgical speed, zero red tape, and senior talent only.",
          "mainEntity": { "@id": "https://acceleratebyte.com/#organization" },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://acceleratebyte.com" },
              { "@type": "ListItem", "position": 2, "name": "About", "item": "https://acceleratebyte.com/about" }
            ]
          }
        },
        {
          "@context": "https://schema.org",
          "@type": "Organization",
          "@id": "https://acceleratebyte.com/#organization",
          "name": "AccelerateByte",
          "foundingDate": "2025",
          "foundingLocation": {
            "@type": "Place",
            "name": "Mumbai, Maharashtra, India"
          },
          "description": "Elite tech studio and premium software development agency. We operate as a technical strike force — surgical precision, no wasted movement, obsession with quality.",
          "slogan": "Code at the speed of business."
        },
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "When was AccelerateByte founded?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "AccelerateByte was founded in 2025 in Mumbai, Maharashtra, India."
              }
            },
            {
              "@type": "Question",
              "name": "What makes AccelerateByte different from other software agencies?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "AccelerateByte eliminates the typical agency overhead: no middle managers, direct access to senior engineers, rapid delivery without red tape, and a genuine partnership approach. Clients work directly with experienced engineers who are invested in their product's success."
              }
            },
            {
              "@type": "Question",
              "name": "Does AccelerateByte work with startups?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. AccelerateByte specialises in working with early-stage startups, helping them launch investor-ready MVPs quickly and scale their products as they grow. We understand startup constraints — both timeline and budget."
              }
            },
            {
              "@type": "Question",
              "name": "Where does AccelerateByte serve clients?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Based in Mumbai, AccelerateByte serves clients globally. Our primary markets are India, the United Kingdom, the United States, and the United Arab Emirates."
              }
            },
            {
              "@type": "Question",
              "name": "Does AccelerateByte use AI in its development process?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. AccelerateByte leverages advanced AI tools internally to write better code faster, perform code reviews, and accelerate testing. This AI-augmented workflow is part of why we deliver 3x faster than traditional agencies."
              }
            }
          ]
        }
      ]} />
      {/* HEADER */}
      <section className="relative pt-32 pb-20 px-4 bg-muted/10 border-b border-border/50">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="animate-hero-fade-in inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-sm font-medium text-primary mb-6" style={{ animationDelay: '0s' }}>
            Our Story
          </div>
          <h1 className="animate-hero-fade-in font-display text-5xl md:text-7xl font-bold mb-6" style={{ animationDelay: '0.1s' }}>
            Built for Speed. <br/>Designed for Scale.
          </h1>
          <p className="animate-hero-fade-in text-xl text-muted-foreground" style={{ animationDelay: '0.2s' }}>
            We founded AccelerateByte in 2025 because we were tired of the traditional agency model: slow timelines, bloated budgets, and mediocre code. We decided to build the elite technical partner we wished existed.
          </p>
        </div>
      </section>

      {/* MISSION & VISION */}
      <section className="py-24 px-4 container mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="font-display text-4xl font-bold mb-6">The technical execution gap is where most startups fail.</h2>
            <p className="text-lg text-muted-foreground mb-6">
              Great ideas are common. The ability to execute those ideas quickly and reliably is rare. We exist to bridge that gap for our clients.
            </p>
            <p className="text-lg text-muted-foreground">
              Based in Mumbai, we serve ambitious companies across the UK, US, EU, and UAE. Our team operates like an elite strike force—surgical precision, no wasted movement, and an obsession with quality.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="bg-card border border-border/50 rounded-2xl p-6 shadow-sm">
              <Terminal className="h-10 w-10 text-primary mb-4" />
              <h3 className="font-display text-xl font-bold mb-2">Modern Stack</h3>
              <p className="text-muted-foreground text-sm">We don't use outdated tools. React, Next.js, Node, and modern cloud architecture.</p>
            </div>
            <div className="bg-card border border-border/50 rounded-2xl p-6 shadow-sm">
              <Zap className="h-10 w-10 text-primary mb-4" />
              <h3 className="font-display text-xl font-bold mb-2">Zero Red Tape</h3>
              <p className="text-muted-foreground text-sm">Direct access to the engineers building your product. No middle managers.</p>
            </div>
            <div className="bg-card border border-border/50 rounded-2xl p-6 shadow-sm">
              <Users className="h-10 w-10 text-primary mb-4" />
              <h3 className="font-display text-xl font-bold mb-2">True Partners</h3>
              <p className="text-muted-foreground text-sm">We don't just write code; we advise on technical strategy and architecture.</p>
            </div>
            <div className="bg-card border border-border/50 rounded-2xl p-6 shadow-sm">
              <Cpu className="h-10 w-10 text-primary mb-4" />
              <h3 className="font-display text-xl font-bold mb-2">AI-Accelerated</h3>
              <p className="text-muted-foreground text-sm">We leverage advanced AI tools internally to write better code, faster.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 px-4 text-center container mx-auto max-w-3xl">
        <h2 className="font-display text-4xl md:text-5xl font-bold mb-6">Let's build something incredible.</h2>
        <p className="text-xl text-muted-foreground mb-10">Our team is ready to accelerate your technical roadmap.</p>
        <Link href="/contact" className="inline-flex h-14 items-center justify-center rounded-md bg-gradient-to-r from-blue-600 to-purple-600 px-8 text-lg font-bold text-white shadow-lg transition-all hover:scale-105">
          Start a Conversation
        </Link>
      </section>
    </div>
  );
}
